import React, { useMemo, useState, useEffect, useCallback } from "react";
import Meta from "../components/Meta";
import Custominput from "../components/Custominput";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";
import { FiEye, FiEyeOff } from "react-icons/fi";

import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "../app/features/user/userSlice";

import kuptoLogo from "../images/logo/kupto logobb-8.png";
import "./styles/login.css";

// ✅ routes
const IDENTIFY_ROUTE = "/identification";

// ✅ session keys
const RETURN_TO_KEY = "kupto_returnTo";
const IDENTITY_KEY = "kupto_identity";
const IDENTITY_TYPE_KEY = "kupto_identity_type";

const ssGet = (k) => {
  try {
    return sessionStorage.getItem(k) || "";
  } catch {
    return "";
  }
};
const ssSet = (k, v) => {
  try {
    sessionStorage.setItem(k, v);
  } catch {}
};
const ssRemove = (k) => {
  try {
    sessionStorage.removeItem(k);
  } catch {}
};

const isValidEmail = (v) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

const UG_CC = "256";

const normalizeUgPhoneToE164 = (raw) => {
  const v = String(raw || "").trim();
  if (!v) return "";

  let s = v.replace(/[^\d+]/g, "");

  if (s.startsWith("00")) s = "+" + s.slice(2);
  if (s.startsWith("+256")) return s;
  if (s.startsWith("256")) return "+" + s;

  if (s.startsWith("0")) {
    const rest = s.replace(/^0+/, "");
    return `+${UG_CC}${rest}`;
  }

  if (/^\d+$/.test(s) && s.length >= 7 && s.length <= 9) {
    return `+${UG_CC}${s}`;
  }

  return s.startsWith("+") ? s : "+" + s;
};

const normalizeIdentity = (raw) => {
  const v = String(raw || "").trim();
  if (!v) return { identity: "", type: "" };
  if (isValidEmail(v)) return { identity: v.toLowerCase(), type: "email" };
  return { identity: normalizeUgPhoneToE164(v), type: "phone" };
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const authLoading = useSelector((state) => state?.auth?.isLoading);
  const isLoading = Boolean(authLoading);

  // from Identify / ProfileDetails
  const stateIdentity = location?.state?.identity || "";
  const stateType = location?.state?.type || "";
  const stateReturnTo = location?.state?.returnTo || "";

  // returnTo: state -> session -> fallback
  const returnTo = useMemo(() => {
    if (typeof stateReturnTo === "string" && stateReturnTo.trim()) {
      return stateReturnTo.trim();
    }
    const ss = ssGet(RETURN_TO_KEY);
    if (ss && ss.trim()) return ss.trim();
    return "/";
  }, [stateReturnTo]);

  // persist returnTo
  useEffect(() => {
    if (returnTo && String(returnTo).trim()) {
      ssSet(RETURN_TO_KEY, String(returnTo).trim());
    }
  }, [returnTo]);

  // persist identity/type and protect route
  useEffect(() => {
    if (stateIdentity && String(stateIdentity).trim()) {
      ssSet(IDENTITY_KEY, String(stateIdentity).trim());
    }
    if (stateType && String(stateType).trim()) {
      ssSet(IDENTITY_TYPE_KEY, String(stateType).trim());
    }

    const ssIdentity = ssGet(IDENTITY_KEY);
    const ssType = ssGet(IDENTITY_TYPE_KEY);

    const hasIdentity = Boolean(
      (stateIdentity && String(stateIdentity).trim()) ||
        (ssIdentity && ssIdentity.trim())
    );
    const hasType = Boolean(
      (stateType && String(stateType).trim()) || (ssType && ssType.trim())
    );

    if (!hasIdentity || !hasType) {
      navigate(IDENTIFY_ROUTE, {
        replace: true,
        state: { returnTo: returnTo || "/" },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateIdentity, stateType, navigate, returnTo]);

  // build identity/type from state or session
  const { identity, type } = useMemo(() => {
    const ssIdentity = ssGet(IDENTITY_KEY);
    const ssType = ssGet(IDENTITY_TYPE_KEY);

    const raw =
      (stateIdentity && String(stateIdentity).trim()) || ssIdentity || "";
    const t = (stateType && String(stateType).trim()) || ssType || "";

    const n = normalizeIdentity(raw);

    return {
      identity: raw ? n.identity : "",
      type: t || n.type || "",
    };
  }, [stateIdentity, stateType]);

  const [password, setPassword] = useState("");
  const [touched, setTouched] = useState(false);
  const [apiErr, setApiErr] = useState("");
  const [showPw, setShowPw] = useState(false);

  const maskedIdentity = useMemo(() => {
    if (!identity) return "";
    if (type === "email") {
      const [u, d] = identity.split("@");
      if (!u || !d) return identity;
      return `${u.slice(0, 1)}${"*".repeat(Math.max(1, u.length - 1))}@${d}`;
    }
    const digits = identity.replace(/[^\d]/g, "");
    if (digits.length < 4) return identity;
    return `***${digits.slice(-3)}`;
  }, [identity, type]);

  const validIdentity = useMemo(() => {
    if (!identity) return false;
    if (type === "email") return isValidEmail(identity);
    const digits = identity.replace(/[^\d]/g, "");
    return digits.length >= 7 && digits.length <= 15;
  }, [identity, type]);

  const showError = touched && (!validIdentity || !password.trim());

  const onBack = useCallback(() => {
    navigate(IDENTIFY_ROUTE, { state: { returnTo: returnTo || "/" } });
  }, [navigate, returnTo]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);
    setApiErr("");

    if (!validIdentity) {
      setApiErr("Your email/phone is not valid. Please go back and change it.");
      return;
    }
    if (!password.trim()) {
      setApiErr("Password is required.");
      return;
    }

    try {
      await dispatch(loginUser({ identity, type, password })).unwrap();

      // clear staged identity
      ssRemove(IDENTITY_KEY);
      ssRemove(IDENTITY_TYPE_KEY);

      navigate(returnTo || "/", { replace: true });
    } catch (err) {
      console.error(err);
      setApiErr(
        typeof err === "string"
          ? err
          : err?.message || "Invalid credentials. Please try again."
      );
    }
  };

  return (
    <>
      <Meta title="Sign in" />

      {isLoading ? (
        <div className="ku-li-topLoader" role="status" aria-label="Loading">
          <span className="ku-li-topTrack" aria-hidden="true">
            <span className="ku-li-topBar" />
          </span>
        </div>
      ) : null}

      <div className="ku-li-wrap">
        <div className="ku-li-shell">
          <div className={`ku-li-split ${isLoading ? "is-busy" : ""}`}>
            <button
              type="button"
              className="ku-li-back ku-li-back--appOnly"
              onClick={onBack}
              aria-label="Go back"
              disabled={isLoading}
            >
              <IoArrowBack />
            </button>

            <div className="ku-li-left">
              <div className="ku-li-brand" aria-hidden="true">
                <div className="ku-li-mark">
                  <img
                    src={kuptoLogo}
                    alt="Kupto"
                    className="ku-li-logo"
                    draggable="false"
                  />
                </div>
              </div>

              <div className="ku-li-head">
                <h1 className="ku-li-title">Sign in</h1>

                {identity ? (
                  <p className="ku-li-sub">
                    Signing in as <b>{maskedIdentity}</b>{" "}
                    <Link
                      className="ku-li-miniLink"
                      to={IDENTIFY_ROUTE}
                      state={{ returnTo: returnTo || "/" }}
                    >
                      Change
                    </Link>
                  </p>
                ) : (
                  <p className="ku-li-sub">Enter your password to continue.</p>
                )}
              </div>

              <form className="ku-li-form" onSubmit={onSubmit} noValidate>
                <div className="ku-li-field">
                  <div className="ku-li-input ku-li-input--readonly">
                    <span className="ku-li-placeholderText">
                      {identity || "—"}
                    </span>
                  </div>

                  {!identity ? (
                    <div className="ku-li-hint">
                      Go to{" "}
                      <Link
                        to={IDENTIFY_ROUTE}
                        state={{ returnTo: returnTo || "/" }}
                      >
                        Identification
                      </Link>{" "}
                      to enter your email/phone.
                    </div>
                  ) : null}
                </div>

                <div className="ku-li-field">
                  <div
                    className={`ku-li-input ku-li-input--pw ${
                      showError && !password ? "is-error" : ""
                    }`}
                  >
                    <Custominput
                      type={showPw ? "text" : "password"}
                      name="password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onBlur={() => setTouched(true)}
                      disabled={isLoading}
                    />

                    <button
                      type="button"
                      className="ku-li-eyeBtn"
                      onClick={() => setShowPw((s) => !s)}
                      aria-label={showPw ? "Hide password" : "Show password"}
                      disabled={isLoading}
                      tabIndex={0}
                    >
                      {showPw ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>

                  <div className="ku-li-actionsRow">
                    <Link
                      className="ku-li-linkBtn"
                      to="/forgot-password"
                      state={{ identity, type, returnTo: returnTo || "/" }}
                    >
                      Forgot password?
                    </Link>
                  </div>
                </div>

                {apiErr ? <div className="ku-li-error">{apiErr}</div> : null}

                <button
                  type="submit"
                  className="ku-li-btn"
                  disabled={
                    isLoading || !password.trim() || !validIdentity || !identity
                  }
                >
                  Continue
                </button>

                <p className="ku-li-legal">
                  By continuing you agree to Kupto’s{" "}
                  <Link to="/terms-and-conditions">Terms and Conditions</Link>
                </p>
              </form>

              <span className="ku-li-glow ku-li-glow--1" />
              <span className="ku-li-glow ku-li-glow--2" />
            </div>

            <div className="ku-li-right ku-li-right--desktopOnly">
              <h3 className="ku-li-rightTitle">Tips</h3>

              <div className="ku-li-tipList">
                <div className="ku-li-tip">
                  <div className="ku-li-tipIcon">🔒</div>
                  <div className="ku-li-tipTxt">
                    <p className="ku-li-tipH">Keep it secure</p>
                    <p className="ku-li-tipP">
                      Never share your password with anyone.
                    </p>
                  </div>
                </div>

                <div className="ku-li-tip">
                  <div className="ku-li-tipIcon">📩</div>
                  <div className="ku-li-tipTxt">
                    <p className="ku-li-tipH">Forgot password?</p>
                    <p className="ku-li-tipP">
                      Use “Forgot password” to reset via email.
                    </p>
                  </div>
                </div>
              </div>

              <div className="ku-li-smallCard">
                <p className="ku-li-smallTitle">New to Kupto?</p>
                <p className="ku-li-smallText">
                  If you don’t have an account, go back and enter a new
                  email/phone to sign up.
                </p>

                <Link
                  to={IDENTIFY_ROUTE}
                  state={{ returnTo: returnTo || "/" }}
                  className="ku-li-smallBtn"
                >
                  Sign Up
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;